import { useMemo } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { articleApi } from '@/modules/articles/api';
import { Article } from '@/modules/articles/model/article';
import ArticleModal from '@/modules/articles/components/articles/article-modal';
import BookmarkButton from '@/modules/articles/components/bookmarks/bookmark-button';
import {
  SearchResultItem,
  SearchResultTypeProvider,
  useSearchActionPermissions,
} from '@/core/search/export';
import { AnalyticsActionType, AnalyticsTypes } from '@/shared/analytics/export';

function BookmarkAction({
  item,
  recordAction,
}: {
  item: SearchResultItem;
  recordAction(actionType: AnalyticsActionType): void;
}) {
  const { canPerformAction } = useSearchActionPermissions();

  // Memoised on the search item so the article identity is stable across re-renders.
  // A fresh literal on every render made BookmarkButton refetch opinions each time the
  // app re-rendered, one request per visible result.
  const bookmarkData = useMemo(
    () =>
      ({
        uri: item.filters?.find(f => f.key === 'externalId')?.value || '',
        headline: item.short_description || '',
        genre: item.filters?.filter(f => f.key === 'genre') || [],
        subject: item.filters?.filter(f => f.key === 'subject') || [],
      }) as unknown as Article,
    [item],
  );

  if (!canPerformAction('bookmark')) return null;

  return (
    <OverlayTrigger
      placement="right"
      delay={{ show: 250, hide: 400 }}
      overlay={<Tooltip id="bookmark-tooltip">Bookmark</Tooltip>}
    >
      <div>
        <BookmarkButton
          article={bookmarkData}
          onToggle={(bookmark: boolean) =>
            recordAction(
              bookmark
                ? AnalyticsActionType.ARTICLE_BOOKMARKED
                : AnalyticsActionType.ARTICLE_UN_BOOKMARKED,
            )
          }
        />
      </div>
    </OverlayTrigger>
  );
}

// Article results in global search: bookmark button, analytics payload and the article modal.
export const articleSearchResultType: SearchResultTypeProvider = {
  async analyticsObject(item, actionType) {
    const article = await articleApi.getArticleBySlug(item.slug);
    return {
      type: AnalyticsTypes.ARTICLE,
      actionType,
      id: article?.uri,
      title: article.headline,
      genres: article.genre.map(g => ({ id: `${g.scheme}:${g.code}`, name: g.name })),
      subjects: article.subject.map(s => ({ id: `${s.scheme}:${s.code}`, name: s.name })),
      authors: article?.byline,
    };
  },
  ItemActions: BookmarkAction,
  async preview(item) {
    const article = await articleApi.getArticleBySlug(item.slug);
    return article.uri;
  },
  Preview: ({ id, onClose }) => <ArticleModal articleId={id} onClose={onClose} />,
};
